import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { isDeletedLocation } from '../../types/locations';
import type { LocationInfo } from '../../types/locations';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../ui/ModalHeader';
import { useTryPermissions } from '../../contexts/PermissionContext';
import api from '../../services/api';


interface LocationInfoModalProps {
  location: LocationInfo | null;
  onClose: () => void;
  onUpdated?: (location: LocationInfo) => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex justify-between gap-3">
    <span className="text-muted whitespace-nowrap">{label}</span>
    <span className="text-heading text-right">{value || '-'}</span>
  </div>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2 py-1 rounded mb-1.5">
    {children}
  </div>
);

/** Mount a fresh editing session when the selected location changes. */
const LocationInfoModal: React.FC<LocationInfoModalProps> = ({ location, ...props }) =>
  location ? <LocationInfoCard key={location.recno} location={location} {...props} /> : null;

const LocationInfoCard: React.FC<Omit<LocationInfoModalProps, 'location'> & { location: LocationInfo }> = ({
  location: initialLocation, onClose, onUpdated,
}) => {
  const [refreshedLocation, setRefreshedLocation] = useState<LocationInfo | null>(null);
  const location = refreshedLocation ?? initialLocation;
  const [error, setError] = useState<string | null>(null);
  const active = useRef(true);
  const permissions = useTryPermissions();
  const canEdit = location.recno > 0 &&
    (typeof permissions?.hasAccess !== 'function' || permissions.hasAccess('/data-management/locations'));

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  // Keep the Pro hotkeys blocked while the nested editor owns keyboard input.
  useAltHotkeys({ C: onClose }, undefined, { layer: true });
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  /**
   * Edit in the Locations maintenance page, in a new tab — the same `?edit=<recno>`
   * deep link `EntityEditLink` uses for the order's Consignee / Broker pencils, so the
   * two pencils on this screen now behave alike. The editor used to open as a drawer
   * nested inside this modal (a dialog within a dialog, over the Legs grid).
   */
  const openEditor = () => {
    window.open(`/data-management/locations?edit=${location.recno}`, '_blank', 'noopener');
  };

  const refreshDetails = useCallback(async () => {
    try {
      const response = await api.get<Record<string, LocationInfo>>('/admin/locations/details', {
        params: { recnos: String(location.recno) },
      });
      if (!active.current) return;
      const updated = response.data[location.recno];
      if (!updated) throw new Error('Location details missing');
      setRefreshedLocation(updated);
      setError(null);
      onUpdated?.(updated);
    } catch {
      if (active.current) setError('Unable to refresh location details.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.recno]);

  // The edit happens in another tab now, so nothing tells this card when it is saved.
  // Re-read on the way back: returning to this window is the one moment the user has
  // finished over there, and a stale card is what they would otherwise be handed.
  useEffect(() => {
    const onFocus = () => { void refreshDetails(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshDetails]);

  // Retired location: the header bar goes red and says DELETED, because the card is the
  // one place a user lands when they ask "what is this stop?" and the answer changes what
  // the stop means. The ring on the grid behind it only works for someone already looking.
  const retired = isDeletedLocation(location);

  const receivingHours =
    location.rcvhours1 || location.rcvhours2
      ? `${location.rcvhours1 || '—'} - ${location.rcvhours2 || '—'}`
      : '';

  return (
    <>
      <div className="fixed inset-0 bg-scrim/30 z-50" onClick={onClose} aria-label="Close location info" />
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      <div
        role="dialog"
        aria-label={`Location details for ${location.lname}`}
        ref={panelRef}
        style={panelStyle}
        className="pointer-events-auto w-[400px] max-h-[80vh] overflow-y-auto bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2">
              <span className="truncate">{location.lname || 'Location'}</span>
              {retired && (
                <span className="shrink-0 rounded bg-white/20 px-1.5 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wide">
                  Deleted
                </span>
              )}
              {canEdit && (
                <button type="button" aria-label="Edit location"
                  title="Edit in table maintenance (opens a new tab)"
                  onClick={openEditor}
                  className="inline-flex shrink-0 items-center justify-center size-6 rounded hover:bg-white/10 disabled:opacity-50">
                  <PencilSquareIcon className="size-4" aria-hidden="true" />
                </button>
              )}
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          // `className` is appended after the bar's own token classes, so the important
          // prefix is what actually beats `bg-cmp-modal-header` (same pattern as the
          // Legs grid's red cell state).
          className={retired ? '!bg-red-700 !border-red-800' : ''}
        />

        <div className="p-4 text-xs space-y-4">
          {error && <div role="alert" className="text-red-600 dark:text-red-400">{error}</div>}
          {/* This location is retired but legs still point at it, so say so where the
              user is already looking rather than only ringing the field behind. */}
          {retired && (
            <div role="alert"
              className="rounded border border-red-300 bg-red-50 px-2 py-1 font-semibold text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-300">
              This location has been deleted in Data Management.
            </div>
          )}

          {/* Record / address header block — V1 popup parity. Only present once
              the backend returns the address columns; old payloads skip it.
              Any address component (street/city/state/zip) surfaces the block —
              V1 always shows Record #, so don't hide it for odd data shapes. */}
          {(location.ladd || location.lcity || location.lstate || location.lzip) && (
            <div className="space-y-1">
              <Row label="Record:" value={String(location.recno)} />
              <Row label="Address:" value={[location.ladd, location.ladd2].filter(Boolean).join(', ')} />
              <Row label="City/State/Zip:" value={[location.lcity, location.lstate, location.lzip].filter(Boolean).join(', ')} />
            </div>
          )}

          {/* Internal Info */}
          <div>
            <SectionHeader>Internal Info</SectionHeader>
            <div className="space-y-1">
              <Row label="Receiving Hours:" value={receivingHours} />
              <Row label="Appt Required:" value={location.apptrequired} />
              <Row label="Drop/Live:" value={location.droplive} />
              {location.communication && <Row label="Communication:" value={location.communication} />}
              {location.lfax && <Row label="Fax:" value={location.lfax} />}
              <div>
                <div className="text-muted mb-0.5">Contact:</div>
                {location.contacts.length === 0 ? (
                  <div className="text-heading pl-2">-</div>
                ) : (
                  <div className="space-y-1 pl-2">
                    {location.contacts.map((ct, i) => (
                      <div key={i} className="text-heading">
                        {ct.type && (
                          <span className="text-muted">{ct.type}: </span>
                        )}
                        {[ct.name, ct.phone, ct.email].filter(Boolean).join(' · ') || '-'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {location.additionalinfo && (
                <div>
                  <div className="text-muted mb-0.5">Additional Info:</div>
                  <div className="text-heading whitespace-pre-wrap pl-2">
                    {location.additionalinfo}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Driver Info */}
          <div>
            <SectionHeader>Driver Info</SectionHeader>
            <div>
              <div className="text-muted mb-0.5">Location Information / Instructions:</div>
              <div className="text-heading whitespace-pre-wrap pl-2">
                {location.specialinstructions || '-'}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default LocationInfoModal;
